import * as React from 'react';
import { useState } from 'react';

import type { BannerColors, Cta, TextLayoutOptions } from '@coursera-web/cds-labs';
import { PromoBarModule } from '@coursera-web/cds-labs';

import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { useCampaignModulesData } from 'bundles/campaigns/context/CampaignModulesDataContext';

export const useShouldShowCampaignPromoBarModule = () => {
  const { campaignData, loading } = useCampaignModulesData();
  return !!campaignData?.promoBarModule?.contentData || loading;
};

const CampaignPromoBarModuleRenderer = () => {
  const [isDismissed, setIsDismissed] = useState<boolean>(false);
  const handleDismiss = () => {
    setIsDismissed(true);
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'promo_bar',
      },
    }
  );

  const track = useTracker();
  const trackClick = (linkTarget: string) => () => {
    track('click_button', {
      button: { name: 'promo_cta', linkURL: linkTarget, linkType: 'internal' },
      pageSection: {
        sectionName: 'promo_bar',
      },
    });
  };

  const content = useCampaignModulesData()?.campaignData?.promoBarModule?.contentData;

  if (!useShouldShowCampaignPromoBarModule()) {
    return null;
  }

  const {
    headline,
    description,
    textLayout,
    backgroundColor,
    bannerColor,
    centerContent,
    primaryCtaText,
    primaryCtaTarget,
    hideBackgroundImages,
  } = content || {};

  let cta: Cta;
  if (primaryCtaText && primaryCtaTarget) {
    cta = {
      text: primaryCtaText,
      href: primaryCtaTarget,
      onClick: trackClick(primaryCtaTarget),
    };
  }

  const getPromoBarModuleProps = () => {
    return {
      headline: headline ?? '',
      description: description ?? '',
      textLayout: textLayout as TextLayoutOptions,
      backgroundColor: backgroundColor ?? 'white',
      centerContent: !!centerContent,
      bannerColor: bannerColor as BannerColors,
      isDismissed,
      onDismiss: handleDismiss,
      cta,
      hideBackgroundImages,
    };
  };

  return (
    <div ref={visibilityTrackingRef}>
      <PromoBarModule {...getPromoBarModuleProps()} />
    </div>
  );
};

export default CampaignPromoBarModuleRenderer;
